<template>
  <nav class="tab-bar" aria-label="Mobile navigation">
    <!-- Home -->
    <a href="/" class="tab-bar__tab" :class="{ 'tab-bar__tab--active': isActive('/') }">
      <i class="fas fa-home"></i>
      <span class="tab-bar__label">Home</span>
    </a>

    <!-- Modules -->
    <a href="/modules" class="tab-bar__tab" :class="{ 'tab-bar__tab--active': isActive('/modules') || isActive('/categories') }">
      <i class="fas fa-book"></i>
      <span class="tab-bar__label">Modules</span>
    </a>

    <!-- Results (or Login if not authenticated) -->
    <a
      v-if="localNavData.is_logged_in"
      href="/results"
      class="tab-bar__tab"
      :class="{ 'tab-bar__tab--active': isActive('/results') }"
    >
      <i class="fas fa-chart-bar"></i>
      <span class="tab-bar__label">Results</span>
    </a>
    <a
      v-else
      href="/users/sign_in"
      class="tab-bar__tab"
    >
      <i class="fas fa-sign-in"></i>
      <span class="tab-bar__label">Login</span>
    </a>

    <!-- Social -->
    <a
      v-if="socialHref"
      :href="socialHref"
      class="tab-bar__tab"
      :class="{ 'tab-bar__tab--active': isSocialActive }"
    >
      <i class="fas fa-user-group"></i>
      <span v-if="socialBadge > 0" class="tab-bar__badge">{{ socialBadge }}</span>
      <span class="tab-bar__label">Social</span>
    </a>

    <!-- More -->
    <button class="tab-bar__tab" @click="toggleMore" :class="{ 'tab-bar__tab--active': showMore }">
      <i class="fas fa-ellipsis"></i>
      <span class="tab-bar__label">More</span>
    </button>

    <!-- More sheet -->
    <Transition name="sheet">
      <div v-if="showMore" class="tab-bar__sheet" @click.self="showMore = false">
        <div class="tab-bar__sheet-content">
          <div class="tab-bar__sheet-handle"></div>

          <!-- Profile -->
          <a :href="profileHref" class="tab-bar__sheet-item" @click="showMore = false">
            <i class="fas fa-user"></i>
            <span>{{ profileLabel }}</span>
          </a>

          <!-- Groups -->
          <a v-if="groupsHref" :href="groupsHref" class="tab-bar__sheet-item" @click="showMore = false">
            <i class="fas fa-people-group"></i>
            <span>Groups</span>
          </a>

          <!-- Admin -->
          <a v-if="localNavData.admin" :href="localNavData.admin.href" class="tab-bar__sheet-item" @click="showMore = false">
            <i class="fas fa-wrench"></i>
            <span>Admin</span>
          </a>

          <!-- Settings -->
          <a v-if="settingsItem" :href="settingsItem.href" class="tab-bar__sheet-item" @click="showMore = false">
            <i :class="['fas', settingsItem.icon]"></i>
            <span>{{ settingsItem.label }}</span>
          </a>

          <div class="tab-bar__sheet-divider"></div>

          <!-- Footer links -->
          <a
            v-for="fl in localNavData.footer_links"
            :key="fl.href"
            :href="fl.href"
            class="tab-bar__sheet-item"
            @click="showMore = false"
          >
            <i :class="['fas', fl.icon]"></i>
            <span>{{ fl.label }}</span>
          </a>

          <div class="tab-bar__sheet-divider"></div>

          <div class="tab-bar__sheet-legal">
            <a href="/privacy">Privacy</a>
            <span>&middot;</span>
            <a href="/terms">Terms</a>
          </div>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNavData } from '../composables/useNavData'

const props = defineProps({
  navData: Object
})

const showMore = ref(false)
const { localNavData, applyNavAuthPayload } = useNavData(props.navData)

const profileItem = computed(() =>
  localNavData.value.rail_items.find(i => i.key === 'profile')
)

const socialItem = computed(() =>
  localNavData.value.rail_items.find(i => i.key === 'social')
)

const settingsItem = computed(() =>
  localNavData.value.rail_items.find(i => i.key === 'settings')
)

const profileHref = computed(() => profileItem.value?.href || '/users/sign_in')
const profileLabel = computed(() => profileItem.value?.label || 'Profile')
const socialHref = computed(() => socialItem.value?.href)
const socialBadge = computed(() => socialItem.value?.badge || 0)
const groupsHref = computed(() => {
  const socialPanel = localNavData.value.panels?.social
  if (socialPanel) {
    const groupsLink = socialPanel.links.find(l => l.label === 'Groups')
    return groupsLink?.href
  }
  return null
})
const isSocialActive = computed(() => {
  const path = window.location.pathname
  return /\/users\/\d+\/(friends|groups)/.test(path)
})

function isActive(prefix) {
  const path = window.location.pathname
  if (prefix === '/') return path === '/'
  return path.startsWith(prefix)
}

function toggleMore() {
  showMore.value = !showMore.value
}

onMounted(() => {
  window.addEventListener('nav:auth-updated', applyNavAuthPayload)
})

onUnmounted(() => {
  window.removeEventListener('nav:auth-updated', applyNavAuthPayload)
})
</script>

<style scoped>
.tab-bar {
  display: none;
}

@media (max-width: 768px) {
  .tab-bar {
    display: flex;
    align-items: center;
    justify-content: space-around;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    /* fixed tab height plus room for the home indicator (viewport-fit=cover) */
    height: calc(var(--tab-bar-height, 56px) + env(safe-area-inset-bottom, 0px));
    background: var(--content-bg);
    border-top: 1px solid var(--card-border);
    z-index: 1000;
    padding-bottom: env(safe-area-inset-bottom, 0);
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
  }
}

.tab-bar__tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 1;
  height: 100%;
  gap: 2px;
  background: none;
  border: none;
  color: var(--primary-high);
  font-size: 1.1em;
  cursor: pointer;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}

.tab-bar__tab--active {
  color: var(--tertiary);
}

.tab-bar__label {
  font-size: 0.568em; /* 10px vs __tab (1.1em of body 16px) */
  font-weight: 500;
}

.tab-bar__badge {
  position: absolute;
  top: 4px;
  right: calc(50% - 16px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--danger);
  color: white;
  font-size: 0.568em; /* 10px vs __tab */
  font-weight: 700;
  line-height: 1;
}

/* Sheet overlay */
.tab-bar__sheet {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: calc(var(--tab-bar-height, 56px) + env(safe-area-inset-bottom, 0px));
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  display: flex;
  align-items: flex-end;
}

.tab-bar__sheet-content {
  width: 100%;
  background: var(--content-bg);
  border-radius: 16px 16px 0 0;
  padding: 12px 16px 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.tab-bar__sheet-handle {
  width: 36px;
  height: 4px;
  background: var(--primary-low);
  border-radius: 2px;
  margin: 0 auto 16px;
}

.tab-bar__sheet-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 8px;
  color: var(--primary);
  text-decoration: none;
  font-size: 1em;
  font-weight: 500;
  border-radius: var(--radius-md);
}

.tab-bar__sheet-item:hover,
.tab-bar__sheet-item:active {
  background: var(--primary-very-low);
}

.tab-bar__sheet-item i {
  width: 24px;
  text-align: center;
  color: var(--primary-high);
}

.tab-bar__sheet-divider {
  height: 1px;
  background: var(--primary-low);
  margin: 8px 0;
}

.tab-bar__sheet-legal {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  font-size: var(--font-down-2);
  color: var(--primary-high);
}

.tab-bar__sheet-legal a {
  color: var(--primary-high);
  text-decoration: none;
}

/* Sheet transition */
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.2s ease;
}

.sheet-enter-active .tab-bar__sheet-content,
.sheet-leave-active .tab-bar__sheet-content {
  transition: transform 0.25s ease;
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter-from .tab-bar__sheet-content,
.sheet-leave-to .tab-bar__sheet-content {
  transform: translateY(100%);
}
</style>
